"use client";

import Link from "next/link";
import { SideNav } from "@/components/SideNav";

export default function GuideClient() {
  return (
    <>
      <SideNav />
      <article className="guide-page">
        <div className="guide-container">
          <nav className="guide-breadcrumb">
            <Link href="/">Home</Link>
            <span>/</span>
            <span>Guide</span>
          </nav>

          <header className="guide-header">
            <h1>How to Accept USDC Payments Without a Merchant Account</h1>
            <p className="guide-intro">
              Traditional payment processing requires bank approvals, merchant
              account applications, and often weeks of waiting. USDC payments on
              the Base blockchain let you accept stable, dollar-denominated
              payments with nothing more than a crypto wallet. Here&apos;s
              everything you need to know.
            </p>
          </header>

          <section className="guide-section">
            <h2>What is USDC?</h2>
            <p>
              USDC (USD Coin) is a{" "}
              <strong>stablecoin pegged 1:1 to the US Dollar</strong>, issued by{" "}
              <a
                href="https://www.circle.com/usdc"
                target="_blank"
                rel="noopener noreferrer"
              >
                Circle
              </a>
              . Unlike volatile cryptocurrencies like Bitcoin or Ethereum, 1
              USDC always equals approximately $1 USD. It&apos;s backed by US
              dollar reserves held in regulated financial institutions.
            </p>
            <p>
              This makes USDC ideal for commerce: you get the benefits of
              blockchain payments (instant, global, low-cost) without the price
              volatility risk.
            </p>
          </section>

          <section className="guide-section">
            <h2>Why Base Blockchain?</h2>
            <p>
              <a
                href="https://base.org"
                target="_blank"
                rel="noopener noreferrer"
              >
                Base
              </a>{" "}
              is a Layer 2 blockchain built on Ethereum by Coinbase. It offers:
            </p>
            <ul className="guide-list">
              <li>
                <strong>Low transaction fees</strong> &mdash; Typically around
                $0.1 per transaction, compared to $5-30 on Ethereum mainnet.
              </li>
              <li>
                <strong>Fast confirmations</strong> &mdash; Transactions confirm
                in seconds, not minutes.
              </li>
              <li>
                <strong>Ethereum security</strong> &mdash; Inherits
                Ethereum&apos;s security model while being much cheaper to use.
              </li>
              <li>
                <strong>Growing ecosystem</strong> &mdash; Supported by major
                wallets including Coinbase Wallet and MetaMask.
              </li>
            </ul>
          </section>

          <section className="guide-section">
            <h2>USDC Payments vs Traditional Payment Processors</h2>
            <div className="guide-comparison">
              <table className="guide-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>USDC on Base</th>
                    <th>Traditional (Stripe, PayPal)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Setup time</td>
                    <td>Minutes</td>
                    <td>Days to weeks</td>
                  </tr>
                  <tr>
                    <td>Merchant account</td>
                    <td>Not required</td>
                    <td>Required</td>
                  </tr>
                  <tr>
                    <td>Payout speed</td>
                    <td>Instant</td>
                    <td>2-7 business days</td>
                  </tr>
                  <tr>
                    <td>Transaction fees</td>
                    <td>5% + ~$0.1 gas</td>
                    <td>2.9% + $0.30 per transaction</td>
                  </tr>
                  <tr>
                    <td>Chargebacks</td>
                    <td>None (final)</td>
                    <td>Possible</td>
                  </tr>
                  <tr>
                    <td>Geographic restrictions</td>
                    <td>None</td>
                    <td>Country-dependent</td>
                  </tr>
                  <tr>
                    <td>Currency</td>
                    <td>USDC (= $1 USD)</td>
                    <td>Local currency</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="guide-section">
            <h2>How to Get Started</h2>

            <h3>1. Get a Wallet</h3>
            <p>
              You need a crypto wallet that supports the Base network. We
              recommend:
            </p>
            <ul className="guide-list">
              <li>
                <a
                  href="https://www.coinbase.com/wallet"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>Coinbase Wallet</strong>
                </a>{" "}
                &mdash; Best for beginners. Easy setup, Base is supported by
                default.
              </li>
              <li>
                <a
                  href="https://metamask.io"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>MetaMask</strong>
                </a>{" "}
                &mdash; Popular choice. Add the Base network manually or through{" "}
                <a
                  href="https://chainlist.org"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ChainList
                </a>
                .
              </li>
            </ul>
            <p>
              Your wallet address (starting with <code>0x...</code>) is your
              payment destination. You&apos;ll use this as your payout address
              when creating payment links.
            </p>

            <h3>2. Create a Payment Link</h3>
            <p>
              With Govilo PayLink, you don&apos;t need to write code or
              integrate an API. Just:
            </p>
            <ol className="guide-list guide-list-ordered">
              <li>Go to the <Link href="/seller">Create PayLink page</Link></li>
              <li>Upload your digital content</li>
              <li>Set your price in USDC</li>
              <li>Enter your wallet address</li>
              <li>Get a shareable payment link</li>
            </ol>

            <h3>3. Share and Get Paid</h3>
            <p>
              Share your payment link anywhere. When a buyer clicks it, they
              connect their wallet, approve the USDC payment, and your content
              is delivered automatically. The USDC goes straight to your wallet
              &mdash; no intermediary holds your funds.
            </p>
          </section>

          <section className="guide-section">
            <h2>How the Payment Works (Technical Overview)</h2>
            <p>
              Under the hood, Govilo uses{" "}
              <a
                href="https://docs.uniswap.org/contracts/permit2/overview"
                target="_blank"
                rel="noopener noreferrer"
              >
                Uniswap&apos;s Permit2
              </a>{" "}
              protocol with EIP-712 signatures. This means:
            </p>
            <ul className="guide-list">
              <li>
                Buyers sign an off-chain authorization (gasless), then submit
                one on-chain transaction.
              </li>
              <li>
                The payment is atomic &mdash; either the full amount transfers
                or nothing happens.
              </li>
              <li>
                Every transaction is recorded on the Base blockchain and can be
                verified by anyone.
              </li>
            </ul>
          </section>

          <section className="guide-section">
            <h2>Converting USDC to Local Currency</h2>
            <p>
              Once you receive USDC in your wallet, you have several options:
            </p>
            <ul className="guide-list">
              <li>
                <strong>Hold as USDC</strong> &mdash; Since it&apos;s
                dollar-pegged, it maintains stable value.
              </li>
              <li>
                <strong>Transfer to Coinbase</strong> &mdash; Send USDC to your
                Coinbase account and sell for your local currency via bank
                transfer.
              </li>
              <li>
                <strong>Use a DEX</strong> &mdash; Swap USDC for other tokens on
                decentralized exchanges.
              </li>
              <li>
                <strong>Spend directly</strong> &mdash; Many services and cards
                now accept USDC for direct spending.
              </li>
            </ul>
          </section>

          <section className="guide-section">
            <h2>Start Accepting Payments Today</h2>
            <p>
              No applications, no approvals, no waiting. Create your first
              payment link in under 2 minutes.
            </p>
            <div className="guide-cta">
              <Link href="/seller" className="btn-primary">
                Create a Payment Link
              </Link>
            </div>
            <p>
              Need help? Check our{" "}
              <Link href="/support">Support &amp; FAQ page</Link> or read our
              guide on{" "}
              <Link href="/guide/sell-digital-content-with-crypto">
                selling digital content with crypto
              </Link>
              .
            </p>
          </section>
        </div>
      </article>
    </>
  );
}
